import { TwinEntity } from '@repo/backend-types';
import { ReactNode, useMemo } from 'react';
import { Box3, MeshStandardMaterial, Vector2, Shape, ShapeGeometry, DoubleSide } from 'three';
import { Entity, TwinEntityType, AppMode } from '@repo/frontend-types';
import {
  convertCoordLocalPoint,
  coordLocalOutlineToVector2,
  getAreaOutlineShape,
  expandOutline,
  ASSET_COLOR,
  topLevelTypeFor,
} from '@repo/ui';

import { useModeContext, useTwinContext } from '@repo/front-end-state';
import { Box } from '../../behaviours/Box';
import { EditView } from '../../behaviours/EditView';

interface Props {
  entity: Entity;
  mapDiv: HTMLDivElement;
  lineage: TwinEntity[];
  depth: number;
  children: ReactNode;
}

// Use exactly the same material settings as other assets
const EQUIPTMENT_METALNESS = 0.3;
const EQUIPTMENT_ROUGHNESS = 1;

const trackSurfaceMaterial = new MeshStandardMaterial({
  metalness: EQUIPTMENT_METALNESS,
  roughness: EQUIPTMENT_ROUGHNESS,
  color: ASSET_COLOR,
  side: DoubleSide,
});

// Function to smooth the track outline for realistic stadium curves
function smoothTrackOutline(points: Vector2[]): Vector2[] {
  if (points.length < 4) return points;

  const smoothedPoints: Vector2[] = [];

  for (let i = 0; i < points.length; i++) {
    const current = points[i];
    const next = points[(i + 1) % points.length];

    // Add the current point
    smoothedPoints.push(current.clone());

    // Add interpolated points between current and next for smooth curves
    const numInterpolations = 4;
    for (let j = 1; j <= numInterpolations; j++) {
      const t = j / (numInterpolations + 1);
      const interpolated = new Vector2(
        current.x + (next.x - current.x) * t,
        current.y + (next.y - current.y) * t,
      );
      smoothedPoints.push(interpolated);
    }
  }

  return smoothedPoints;
}

export const RunningTrackArea = ({ entity, mapDiv, depth, lineage, children }: Props) => {
  const { appMode } = useModeContext();
  const { hasFloorLevel, multifloor } = useTwinContext();

  const { centre, areaBlock, trackSurfaceGeometry } = useMemo(() => {
    if (entity.boundaries === undefined || entity.boundaries === null) {
      throw new Error(
        `Running track area with id ${entity.id} has no boundaries. We cannot render it`,
      );
    }

    const { centre, areaBlock } = getAreaOutlineShape(entity.boundaries.polygons, depth);

    let trackSurfaceGeometry = null;

    try {
      const polygon = entity.boundaries.polygons[0];
      const worldSpaceOutline = coordLocalOutlineToVector2(polygon.coordinates[0].points);

      const trackPoints = expandOutline(worldSpaceOutline, -0.03 - depth / 4000);

      if (trackPoints.length >= 3) {
        const smoothedTrackPoints = smoothTrackOutline(trackPoints);
        const trackShape = new Shape(smoothedTrackPoints);
        trackSurfaceGeometry = new ShapeGeometry(trackShape);
        trackSurfaceGeometry.rotateX(-Math.PI / 2);
        trackSurfaceGeometry.computeBoundingBox();
      }
    } catch (error) {
      console.warn('Could not create track surface geometry:', error);
    }

    return { centre, areaBlock, trackSurfaceGeometry };
  }, [entity, depth]);

  const labelPosition = convertCoordLocalPoint(centre, 1.5);

  const scaledBoundingBox = useMemo(() => {
    // Vector3.multiplyScalar mutates in place. Clone first so we don't
    // corrupt the shared areaBlock.boundingBox — see Area.tsx for the
    // full explanation. Label3D positioning is unchanged (scaledBoundingBox
    // values remain 0.01× the original); only the side effect on the
    // shared geometry bbox is fixed.
    return new Box3(
      areaBlock.boundingBox!.min.clone().multiplyScalar(0.01),
      areaBlock.boundingBox!.max.clone().multiplyScalar(0.01),
    );
  }, [areaBlock]);

  return (
    <group position={[0, depth / 4000, 0]}>
      {trackSurfaceGeometry && (
        <mesh
          geometry={trackSurfaceGeometry}
          material={trackSurfaceMaterial}
          position={[0, 0.01, 0]}
          receiveShadow
          castShadow
        />
      )}

      {appMode === AppMode.EDIT ? (
        <EditView mapDiv={mapDiv} lineage={lineage} entity={entity} geometry={areaBlock} />
      ) : (
        <Box
          mapDiv={mapDiv}
          lineage={lineage}
          entity={entity}
          geometry={areaBlock}
          entityType={TwinEntityType.ZONE}
          labelBoundingBox={scaledBoundingBox}
          labelPosition={labelPosition}
          topLevelType={topLevelTypeFor({ hasFloorLevel, multifloor })}
        />
      )}
      {children}
    </group>
  );
};
